import { Box, CircularProgress } from "@mui/material";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import { useV2Theme } from "../../hooks/useV2Theme";
import { useBulkUpdate, useInteractionTags } from "../../../hooks/useInteraction";
import type Interaction from "../../../types/interaction/Interaction";
import ViewPickerInput from "../shared/ViewPickerInput";
import type {
  BulkUpdateInteractionsRequest,
  BulkUpdateInteractionsResponse,
  BulkUpdateItem,
  BulkUpdateItemProperties,
  CollectionUpdate,
} from "../../../types/interaction/BulkUpdate";
import {
  flyoutInputSx,
  flyoutLabelSx,
  tabSx,
  closeButtonSx,
  sectionBadgeSx,
  chipSx,
  chipRemoveBtnSx,
  inlineDrawerSx,
  drawerHeaderSx,
  drawerTitleSx,
  drawerTabBarSx,
  drawerScrollAreaSx,
} from "../shared/crudStyles";
import type { TabId } from "../shared/crudStyles";
import { computeTagSuggestions } from "../../helpers/tagSuggestions";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BulkEditDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly interactions: Interaction[];
  readonly customerId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BulkEditDrawer({
  open,
  onClose,
  interactions,
  customerId,
}: BulkEditDrawerProps) {
  const { t } = useTranslation(["common", "search"]);
  const { mode } = useV2Theme();
  const { user } = useContext(AuthContext);

  // -- Permission & feature flag checks ------------------------------------
  const canEdit = user?.hasPermission("interactions:Update") ?? false;
  const hasViews = user?.hasFeatureFlag("Views") ?? false;

  // -- Legal hold check ----------------------------------------------------
  const hasLegalHold = useMemo(
    () => interactions.some((i) => i.legalHold),
    [interactions],
  );

  // -- Tab state -----------------------------------------------------------
  const [activeTab, setActiveTab] = useState<TabId>("retention");

  // -- Retention form state ------------------------------------------------
  const [retentionDate, setRetentionDate] = useState("");
  const [removeRetention, setRemoveRetention] = useState(false);
  const [applyLegalHold, setApplyLegalHold] = useState(false);

  // -- Tags form state -----------------------------------------------------
  const [tagsToAddInput, setTagsToAddInput] = useState("");
  const [addTagsFocused, setAddTagsFocused] = useState(false);
  const [tagsToAdd, setTagsToAdd] = useState<string[]>([]);
  const [tagsToRemoveInput, setTagsToRemoveInput] = useState("");
  const [removeTagsFocused, setRemoveTagsFocused] = useState(false);
  const [tagsToRemove, setTagsToRemove] = useState<string[]>([]);

  const { data: availableTags } = useInteractionTags(customerId);

  // -- Views form state ----------------------------------------------------
  const [viewsToAdd, setViewsToAdd] = useState<string[]>([]);
  const [viewsToRemove, setViewsToRemove] = useState<string[]>([]);

  // -- API -----------------------------------------------------------------
  const bulkUpdateMutation = useBulkUpdate();
  const { isPending } = bulkUpdateMutation;

  const [apiResponse, setApiResponse] =
    useState<BulkUpdateInteractionsResponse | undefined>(undefined);
  const [lastRequest, setLastRequest] =
    useState<BulkUpdateInteractionsRequest | null>(null);

  // -- Derived -------------------------------------------------------------
  const todayStr = new Date().toISOString().slice(0, 16); // for datetime-local min

  const hasChanges =
    retentionDate !== "" ||
    removeRetention ||
    applyLegalHold ||
    tagsToAdd.length > 0 ||
    tagsToRemove.length > 0 ||
    viewsToAdd.length > 0 ||
    viewsToRemove.length > 0;

  const confirmDisabled = !hasChanges || hasLegalHold || !canEdit || isPending;

  // -- Helpers: tag chips --------------------------------------------------
  const addTag = (list: string[], setList: (v: string[]) => void, input: string, setInput: (v: string) => void) => {
    const trimmed = input.trim();
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setInput("");
  };

  const removeItem = (list: string[], setList: (v: string[]) => void, item: string) => {
    setList(list.filter((t) => t !== item));
  };

  // -- Build request -------------------------------------------------------
  const buildRequest = useCallback((): BulkUpdateInteractionsRequest => {
    const changes: BulkUpdateItem[] = interactions.map((interaction) => {
      const tags: CollectionUpdate | undefined =
        tagsToAdd.length > 0 || tagsToRemove.length > 0
          ? {
              ...(tagsToAdd.length > 0 && { add: tagsToAdd }),
              ...(tagsToRemove.length > 0 && { remove: tagsToRemove }),
            }
          : undefined;

      const views: CollectionUpdate | undefined =
        viewsToAdd.length > 0 || viewsToRemove.length > 0
          ? {
              ...(viewsToAdd.length > 0 && { add: viewsToAdd }),
              ...(viewsToRemove.length > 0 && { remove: viewsToRemove }),
            }
          : undefined;

      let retentionTimestamp: number | undefined;
      if (!applyLegalHold) {
        if (removeRetention) {
          retentionTimestamp = 0;
        } else if (retentionDate) {
          retentionTimestamp = Math.floor(new Date(retentionDate).getTime() / 1000);
        }
      }

      const updates: BulkUpdateItemProperties = {
        ...(retentionTimestamp !== undefined && { retentionDate: retentionTimestamp }),
        ...(applyLegalHold && { legalHold: true }),
        ...(tags && { tags }),
        ...(views && { views }),
      };

      return { interactionId: interaction.interactionId, updates };
    }).filter((item) => Object.keys(item.updates).length > 0);

    return { customerId, changes };
  }, [
    interactions, customerId, retentionDate, removeRetention,
    applyLegalHold, tagsToAdd, tagsToRemove, viewsToAdd, viewsToRemove,
  ]);

  // -- Submit --------------------------------------------------------------
  const handleSubmit = async () => {
    const request = buildRequest();
    if (request.changes.length === 0) return; // Nothing to update
    setLastRequest(request);
    try {
      const response = await bulkUpdateMutation.mutateAsync(request);
      setApiResponse(response);
    } catch {
      setApiResponse(undefined);
    }
  };

  // -- Retry failed --------------------------------------------------------
  const handleRetryFailed = async () => {
    if (!lastRequest || !apiResponse) return;
    const failedIdSet = new Set(
      apiResponse.results
        .filter((r) => r.status !== "OK" && r.status !== "BadRequest")
        .map((r) => r.interactionId),
    );
    const failedChanges = lastRequest.changes.filter((c) =>
      failedIdSet.has(c.interactionId),
    );
    const retryRequest: BulkUpdateInteractionsRequest = {
      customerId: lastRequest.customerId,
      changes: failedChanges,
    };
    setLastRequest(retryRequest);
    try {
      const response = await bulkUpdateMutation.mutateAsync(retryRequest);
      setApiResponse(response);
    } catch {
      // leave existing response
    }
  };

  // -- Reset on close ------------------------------------------------------
  const handleClose = () => {
    setActiveTab("retention");
    setRetentionDate("");
    setRemoveRetention(false);
    setApplyLegalHold(false);
    setTagsToAddInput("");
    setTagsToAdd([]);
    setAddTagsFocused(false);
    setTagsToRemoveInput("");
    setTagsToRemove([]);
    setRemoveTagsFocused(false);
    setViewsToAdd([]);
    setViewsToRemove([]);
    setApiResponse(undefined);
    setLastRequest(null);
    onClose();
  };

  // -- Response stats ------------------------------------------------------
  const successCount = apiResponse
    ? apiResponse.results.filter((r) => r.status === "OK").length
    : 0;
  const failedResults = apiResponse
    ? apiResponse.results.filter((r) => r.status !== "OK")
    : [];

  // -- Tag suggestion filtering --------------------------------------------
  const filteredAddSuggestions = computeTagSuggestions({
    availableTags,
    currentTags: tagsToAdd,
    inputValue: tagsToAddInput,
    isFocused: addTagsFocused,
  });

  // Unique tags across all selected interactions (for remove suggestions)
  const selectedInteractionTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const interaction of interactions) {
      if (interaction.tags) {
        for (const tag of interaction.tags) tagSet.add(tag);
      }
    }
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [interactions]);

  const selectedInteractionViewIds = useMemo(() => {
    const set = new Set<string>();
    for (const i of interactions) {
      for (const v of i.views ?? []) set.add(v);
    }
    return Array.from(set);
  }, [interactions]);

  const filteredRemoveSuggestions = useMemo(() => {
    const source = selectedInteractionTags;
    if (!tagsToRemoveInput.trim()) {
      // Show all tags from selected interactions (minus already chosen)
      return source.filter((t) => !tagsToRemove.includes(t));
    }
    const lower = tagsToRemoveInput.toLowerCase();
    return source
      .filter((t) => t.toLowerCase().includes(lower) && !tagsToRemove.includes(t));
  }, [selectedInteractionTags, tagsToRemoveInput, tagsToRemove]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!open) return null;

  return (
    <Box sx={inlineDrawerSx("bulkEditSlideIn")}>
        {/* ---- Header ---- */}
        <Box sx={drawerHeaderSx}>
          <Box sx={drawerTitleSx}>
            {t("common:bulkEditTitle", { count: interactions.length, plural: interactions.length === 1 ? "" : "s" })}
          </Box>
          <Box component="button" onClick={handleClose} sx={closeButtonSx}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 16, height: 16 }}>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Box>
        </Box>

        {/* ---- Tabs ---- */}
        <Box sx={drawerTabBarSx}>
          <Box component="button" onClick={() => setActiveTab("retention")} sx={tabSx(activeTab === "retention")}>
            {t("common:retention")}
          </Box>
          <Box component="button" onClick={() => setActiveTab("tags")} sx={tabSx(activeTab === "tags")}>
            {t("common:tags")}
          </Box>
          {hasViews && (
            <Box component="button" onClick={() => setActiveTab("views")} sx={tabSx(activeTab === "views")}>
              {t("common:views")}
            </Box>
          )}
        </Box>

        {/* ---- Scrollable Content ---- */}
        <Box sx={drawerScrollAreaSx}>
          {/* Legal hold warning */}
          {hasLegalHold && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                borderRadius: "8px",
                backgroundColor: "rgba(251,113,133,0.1)",
                border: "1px solid rgba(251,113,133,0.3)",
                color: "var(--signal-rose)",
                fontSize: "0.8125rem",
                fontWeight: 500,
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 16, height: 16, flexShrink: 0 }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {t("common:legalHoldBulkEditWarning")}
            </Box>
          )}

          {/* ---- Retention Tab ---- */}
          {activeTab === "retention" && (
            <>
              <Box>
                <Box sx={flyoutLabelSx}>{t("common:retentionDate")}</Box>
                <Box
                  component="input"
                  type="datetime-local"
                  value={retentionDate}
                  min={todayStr}
                  disabled={removeRetention || applyLegalHold}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRetentionDate(e.target.value)}
                  sx={{
                    ...flyoutInputSx,
                    colorScheme: mode === "dark" ? "dark" : "light",
                    opacity: (removeRetention || applyLegalHold) ? 0.4 : 1,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: applyLegalHold ? "default" : "pointer",
                  userSelect: "none",
                  opacity: applyLegalHold ? 0.4 : 1,
                }}
                onClick={() => {
                  if (applyLegalHold) return;
                  setRemoveRetention(!removeRetention);
                  if (!removeRetention) setRetentionDate("");
                }}
              >
                <Box
                  component="input"
                  type="checkbox"
                  checked={removeRetention}
                  onChange={() => {
                    if (applyLegalHold) return;
                    setRemoveRetention(!removeRetention);
                    if (!removeRetention) setRetentionDate("");
                  }}
                  sx={{ accentColor: "var(--accent)", width: 16, height: 16, cursor: "pointer" }}
                />
                <Box sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
                  {t("common:removeRetentionDate")}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => {
                  const next = !applyLegalHold;
                  setApplyLegalHold(next);
                  if (next) {
                    setRetentionDate("");
                    setRemoveRetention(false);
                  }
                }}
              >
                <Box
                  component="input"
                  type="checkbox"
                  checked={applyLegalHold}
                  onChange={() => {
                    const next = !applyLegalHold;
                    setApplyLegalHold(next);
                    if (next) {
                      setRetentionDate("");
                      setRemoveRetention(false);
                    }
                  }}
                  sx={{ accentColor: "var(--accent)", width: 16, height: 16, cursor: "pointer" }}
                />
                <Box sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
                  {t("common:applyLegalHold")}
                </Box>
              </Box>

              {applyLegalHold && (
                <Box
                  sx={{
                    fontSize: "0.8125rem",
                    color: "var(--text-tertiary)",
                    fontStyle: "italic",
                    mt: "-8px",
                  }}
                >
                  {t("common:retentionDateDisabledByHoldBulk")}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  backgroundColor: "var(--accent-dim)",
                  border: "1px solid var(--accent-glow)",
                  color: "var(--text-secondary)",
                  fontSize: "0.8125rem",
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={2} style={{ width: 16, height: 16, flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                {t("common:legalHoldRemovalNotPermitted")}
              </Box>
            </>
          )}

          {/* ---- Tags Tab ---- */}
          {activeTab === "tags" && (
            <>
              {/* Add Tags */}
              <Box>
                <Box sx={{ ...flyoutLabelSx, display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box component="span" sx={sectionBadgeSx("add")}>+</Box>
                  {t("common:addTags")}
                </Box>
                <Box sx={{ position: "relative" }}>
                  <Box
                    component="input"
                    type="text"
                    name="ck-bulk-add-tag-input"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    inputMode="text"
                    // Block password managers from offering to fill / save this field.
                    data-1p-ignore=""
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    placeholder={t("common:typeTagPressEnter")}
                    value={tagsToAddInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagsToAddInput(e.target.value)}
                    onFocus={() => setAddTagsFocused(true)}
                    onBlur={() => setAddTagsFocused(false)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag(tagsToAdd, setTagsToAdd, tagsToAddInput, setTagsToAddInput);
                      } else if (e.key === "Escape") {
                        setAddTagsFocused(false);
                      }
                    }}
                    sx={flyoutInputSx}
                  />
                  {/* Autocomplete suggestions */}
                  {filteredAddSuggestions.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        mt: "4px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-mid)",
                        backgroundColor: "var(--bg-raised)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        maxHeight: 180,
                        overflowY: "auto",
                      }}
                    >
                      {filteredAddSuggestions.map((tag) => (
                        <Box
                          key={tag}
                          component="button"
                          // onMouseDown + preventDefault so the input doesn't blur
                          // before the suggestion is added (which would hide the dropdown).
                          onMouseDown={(e: React.MouseEvent) => {
                            e.preventDefault();
                            if (!tagsToAdd.includes(tag)) setTagsToAdd([...tagsToAdd, tag]);
                            setTagsToAddInput("");
                          }}
                          sx={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            px: "12px",
                            py: "8px",
                            fontSize: "0.8125rem",
                            fontFamily: "inherit",
                            color: "var(--text-primary)",
                            bgcolor: "transparent",
                            border: "none",
                            cursor: "pointer",
                            "&:hover": { bgcolor: "var(--bg-hover)" },
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                {/* Selected tags to add */}
                {tagsToAdd.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }}>
                    {tagsToAdd.map((tag) => (
                      <Box key={tag} sx={chipSx("add")}>
                        {tag}
                        <Box component="button" onClick={() => removeItem(tagsToAdd, setTagsToAdd, tag)} sx={chipRemoveBtnSx}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ width: 10, height: 10 }}>
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Remove Tags */}
              <Box>
                <Box sx={{ ...flyoutLabelSx, display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box component="span" sx={sectionBadgeSx("remove")}>-</Box>
                  {t("common:removeTags")}
                </Box>
                <Box sx={{ position: "relative" }}>
                  <Box
                    component="input"
                    type="text"
                    name="ck-bulk-remove-tag-input"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    inputMode="text"
                    // Block password managers from offering to fill / save this field.
                    data-1p-ignore=""
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    placeholder={selectedInteractionTags.length > 0 ? t("common:selectOrTypeTag") : t("common:noTagsOnSelected")}
                    value={tagsToRemoveInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagsToRemoveInput(e.target.value)}
                    onFocus={() => setRemoveTagsFocused(true)}
                    onBlur={() => setTimeout(() => setRemoveTagsFocused(false), 150)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag(tagsToRemove, setTagsToRemove, tagsToRemoveInput, setTagsToRemoveInput);
                      }
                    }}
                    sx={flyoutInputSx}
                  />
                  {/* Suggestions from tags on selected interactions */}
                  {removeTagsFocused && filteredRemoveSuggestions.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        mt: "4px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-mid)",
                        backgroundColor: "var(--bg-raised)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        maxHeight: 180,
                        overflowY: "auto",
                      }}
                    >
                      {filteredRemoveSuggestions.map((tag) => (
                        <Box
                          key={tag}
                          component="button"
                          onClick={() => {
                            if (!tagsToRemove.includes(tag)) setTagsToRemove([...tagsToRemove, tag]);
                            setTagsToRemoveInput("");
                          }}
                          sx={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            px: "12px",
                            py: "8px",
                            fontSize: "0.8125rem",
                            fontFamily: "inherit",
                            color: "var(--text-primary)",
                            bgcolor: "transparent",
                            border: "none",
                            cursor: "pointer",
                            "&:hover": { bgcolor: "var(--bg-hover)" },
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                {/* Selected tags to remove */}
                {tagsToRemove.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }}>
                    {tagsToRemove.map((tag) => (
                      <Box key={tag} sx={chipSx("remove")}>
                        {tag}
                        <Box component="button" onClick={() => removeItem(tagsToRemove, setTagsToRemove, tag)} sx={chipRemoveBtnSx}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ width: 10, height: 10 }}>
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* ---- Views Tab ---- */}
          {activeTab === "views" && hasViews && (
            <>
              {/* Add Views */}
              <Box>
                <Box sx={{ ...flyoutLabelSx, display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box component="span" sx={sectionBadgeSx("add")}>+</Box>
                  {t("common:addViews")}
                </Box>
                <ViewPickerInput
                  customerId={customerId}
                  selectedViewIds={viewsToAdd}
                  onChange={setViewsToAdd}
                  chipVariant="add"
                />
              </Box>

              {/* Remove Views */}
              <Box>
                <Box sx={{ ...flyoutLabelSx, display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box component="span" sx={sectionBadgeSx("remove")}>-</Box>
                  {t("common:removeViews")}
                </Box>
                <ViewPickerInput
                  customerId={customerId}
                  selectedViewIds={viewsToRemove}
                  onChange={setViewsToRemove}
                  candidateViewIds={selectedInteractionViewIds}
                  chipVariant="remove"
                />
              </Box>
            </>
          )}
        </Box>

        {/* ---- Footer ---- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-subtle)",
            flexShrink: 0,
          }}
        >
          {/* Response results */}
          {apiResponse && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {successCount > 0 && (
                <Box
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: "var(--accent)",
                  }}
                >
                  {t("common:interactionsUpdatedSuccessfully", { count: successCount, plural: successCount === 1 ? "" : "s" })}
                </Box>
              )}
              {failedResults.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      color: "var(--signal-rose)",
                      mb: "4px",
                    }}
                  >
                    {t("common:interactionsFailed", { count: failedResults.length, plural: failedResults.length === 1 ? "" : "s" })}
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 120,
                      overflowY: "auto",
                      borderRadius: "6px",
                      border: "1px solid rgba(251,113,133,0.2)",
                      backgroundColor: "rgba(251,113,133,0.05)",
                      p: "8px",
                    }}
                  >
                    {failedResults.map((r) => (
                      <Box
                        key={r.interactionId}
                        sx={{
                          fontSize: "0.75rem",
                          color: "var(--text-secondary)",
                          py: "2px",
                          fontFamily: "monospace",
                        }}
                      >
                        {r.interactionId}: {r.errorMessage || r.status}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            {apiResponse && failedResults.length > 0 && (
              <Box
                component="button"
                onClick={handleRetryFailed}
                disabled={isPending}
                sx={{
                  px: "16px",
                  py: "8px",
                  borderRadius: "8px",
                  border: "1px solid rgba(251,113,133,0.4)",
                  backgroundColor: "rgba(251,113,133,0.1)",
                  color: "var(--signal-rose)",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(251,113,133,0.2)" },
                  "&:disabled": { opacity: 0.4, cursor: "default" },
                }}
              >
                {t("common:retryFailed")}
              </Box>
            )}
            <Box
              component="button"
              onClick={handleClose}
              sx={{
                px: "20px",
                py: "8px",
                borderRadius: "8px",
                border: "1px solid var(--border-mid)",
                backgroundColor: "transparent",
                color: "var(--text-secondary)",
                fontSize: "0.8125rem",
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: "pointer",
                "&:hover": { backgroundColor: "var(--bg-hover)" },
              }}
            >
              {apiResponse ? t("common:close") : t("common:cancel")}
            </Box>
            {!apiResponse && (
              <Box
                component="button"
                onClick={handleSubmit}
                disabled={confirmDisabled}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  px: "20px",
                  py: "8px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "var(--accent)",
                  color: "#FFFFFF",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.9 },
                  "&:disabled": { opacity: 0.4, cursor: "default" },
                }}
              >
                {isPending && <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />}
                {t("common:confirm")}
              </Box>
            )}
          </Box>
        </Box>
    </Box>
  );
}
